import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import { type OrgExamType } from "@petergok/blue-collar-shared";
import { buildOrgLandingUrl, getOrgExamConfig } from "@/utils/orgPurchases/config";
import { showToast } from "@/lib/toast";

interface ShareEmployerLinkProps {
  examType: OrgExamType;
  variant?: "card" | "settings";
}

// Mirrors BlueCollarApp/src/images/perks-instructor.svg so the web entry point
// matches the mobile org-purchase button visually.
function InstructorIcon({ className }: { className?: string }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4 8C3.44772 8 3 8.44772 3 9V19C3 19.5523 3.44772 20 4 20H20C20.5523 20 21 19.5523 21 19V9C21 8.44772 20.5523 8 20 8H4ZM1 9C1 7.34315 2.34315 6 4 6H20C21.6569 6 23 7.34315 23 9V19C23 20.6569 21.6569 22 20 22H4C2.34315 22 1 20.6569 1 19V9Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.87868 2.87868C8.44129 2.31607 9.20435 2 10 2H14C14.7956 2 15.5587 2.31607 16.1213 2.87868C16.6839 3.44129 17 4.20435 17 5V21C17 21.5523 16.5523 22 16 22C15.4477 22 15 21.5523 15 21V5C15 4.73478 14.8946 4.48043 14.7071 4.29289C14.5196 4.10536 14.2652 4 14 4H10C9.73478 4 9.48043 4.10536 9.29289 4.29289C9.10536 4.48043 9 4.73478 9 5V21C9 21.5523 8.55228 22 8 22C7.44772 22 7 21.5523 7 21V5C7 4.20435 7.31607 3.44129 7.87868 2.87868Z"
        fill="currentColor"
      />
    </svg>
  );
}

export default function ShareEmployerLink({ examType, variant = "card" }: ShareEmployerLinkProps) {
  const { t } = useTranslation();
  const [isSharing, setIsSharing] = useState(false);
  const exam = getOrgExamConfig(examType);

  async function copyToClipboard(text: string) {
    if (!navigator.clipboard) {
      throw new Error("Clipboard is unavailable.");
    }

    await navigator.clipboard.writeText(text);
    showToast({ title: t("profile.share_link_copied") });
  }

  async function handleShare() {
    const url = buildOrgLandingUrl(examType);
    const text = `${t("profile.share_link_share_message", { examLabel: exam.label })}${url}`;

    setIsSharing(true);
    try {
      if (navigator.share) {
        await navigator.share({
          title: t("profile.share_link_share_sheet_title"),
          text,
          url,
        });
      } else {
        await copyToClipboard(text);
      }
    } catch (err) {
      if (!(err instanceof DOMException && err.name === "AbortError")) {
        try {
          await copyToClipboard(text);
        } catch {
          showToast({ title: t("profile.share_link_copy_failed") });
        }
      }
    } finally {
      setIsSharing(false);
    }
  }

  return (
    <button
      type="button"
      onClick={() => void handleShare()}
      disabled={isSharing}
      className={variant === "settings"
        ? "flex min-h-[72px] w-full items-center justify-between gap-4 px-6 text-left transition-colors hover:bg-[#fafafa] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--dp-brand-button)] disabled:opacity-60"
        : "px-4 py-3 bg-white rounded-xl shadow-[0px_0px_8px_0px_rgba(0,0,0,0.08)] flex justify-between items-center disabled:opacity-60"
      }
    >
      <div className="flex items-center gap-4">
        <InstructorIcon className={variant === "settings" ? "h-5 w-5 text-[#5b616d]" : "h-6 w-6 text-[#9A9A9A]"} />
        <span className={variant === "settings" ? "text-base font-medium leading-6 text-[#0a0c11]" : "text-black text-sm font-normal font-inter"}>
          {t("profile.share_link_button_label")}
        </span>
      </div>
      <ChevronRight className={variant === "settings" ? "h-5 w-5 text-[#5b616d]" : "w-6 h-6 text-black"} />
    </button>
  );
}
